import React from 'react';

/**
 * Global error boundary. Prevents a single component render crash from blanking
 * the whole app (white screen). Shows a friendly bilingual fallback with a
 * reload action and logs the error for diagnostics.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surfaced in production logs (console.error is kept in prod builds)
    console.error('ErrorBoundary caught an error:', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDe = (navigator.language || 'de').toLowerCase().startsWith('de');
      return (
        <div
          data-testid="app-error-boundary"
          className="min-h-screen flex items-center justify-center bg-background px-6 py-16"
        >
          <div className="max-w-md w-full text-center space-y-5">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#C9A23C]/15 flex items-center justify-center">
              <svg className="h-7 w-7 text-[#8a6d10]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-foreground">
              {isDe ? 'Etwas ist schiefgelaufen' : 'Something went wrong'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isDe
                ? 'Dieser Bereich konnte nicht geladen werden. Bitte lade die Seite neu. Wenn das Problem bestehen bleibt, kontaktiere bitte den Support.'
                : 'This section could not be loaded. Please reload the page. If the problem persists, please contact support.'}
            </p>
            <button
              type="button"
              data-testid="error-boundary-reload"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center rounded-lg bg-[#C9A23C] px-5 py-2.5 text-sm font-semibold text-[#1a1a1a] transition-colors hover:bg-[#b8902f]"
            >
              {isDe ? 'Seite neu laden' : 'Reload page'}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
